import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { TOAST } from "@constants/index";
import queryKeys from "../configs/react-query/query-key";
import { normalizeResponseData } from "../utils/common";
import { useMutationData } from "./useMutationData";
import { getClarityAirVerificationErrorMessage, getVerificationUploadErrorMessage } from "@utils/error";
import { showErrorToast } from "@configs/react-toastify/snackbar";
import useSuccessToast from "@configs/react-toastify/snackbar";
export const useClarityAirVerificationRequest = () => {
  const { projectSeq } = useParams();
  const [verificationData, setVerificationData] = useState([]);
  const { t } = useTranslation();
  const isMountedRef = useRef(true);
  const pollTimersBySeqRef = useRef(new Map());
  const clearPollTimerForSeq = (seq) => {
    const key = String(seq);
    const id = pollTimersBySeqRef.current.get(key);
    if (id != null) {
      clearTimeout(id);
      pollTimersBySeqRef.current.delete(key);
    }
  };

  const cleanup = () => {
    isMountedRef.current = false;
    pollTimersBySeqRef.current.forEach((id) => clearTimeout(id));
    pollTimersBySeqRef.current.clear();
  };

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  const checkDuplicateVerification = useMutationData(
    queryKeys.checkDuplicateVerificationUpload,
    {
      method: "POST",
    },
    {
      onSuccess: (response, variables) => {
        // Extract filename from FormData if possible
        const formData = variables;
        const file = formData.get("file");
        const fileName = file?.name || "Unknown file";
      },
      onError: (error, variables) => {
        // Extract filename from FormData if possible
        const formData = variables;
        const file = formData.get("file");
        const fileName = file?.name || "Unknown file";
      },
    },
  );

  // New mutation for duplicate check with full URL
  const checkDuplicateVerificationWithParams = useMutationData(
    (data) => {
      const url = `${queryKeys.checkDuplicateVerificationUpload}?projectId=${projectSeq}&verificationType=clarityair`;
      return url;
    },
    {
      method: "POST",
    },
    {
      onSuccess: (response, variables) => {
        // Extract filename from FormData if possible
        const formData = variables;
        const file = formData.get("file");
        const fileName = file?.name || "Unknown file";
      },
      onError: (error, variables) => {
        // Extract filename from FormData if possible
        const formData = variables;
        const file = formData.get("file");
        const fileName = file?.name || "Unknown file";
      },
    },
  );

  const handleSubmitVerificationRequest = async (data, setProcessingRowId, onDuplicateFound) => {
    try {
      // First check for duplicate file
      const maxFileSize = 1024 * 1024 * 1024; // 1GB in bytes

      if (data.verifFile[0].size > maxFileSize) {
        showErrorToast(t("common.error"), t("project.verification.file-size-exceeded"));
        return;
      }
      const checksumFormData = new FormData();
      checksumFormData.append("file", data.verifFile[0]);

      const response = await checkDuplicateVerificationWithParams.mutateAsync(checksumFormData);
      const duplicatedSeq = response?.duplicateSeq || 0;

      if (duplicatedSeq > 0) {
        // Call the callback to show confirmation modal with both options
        if (onDuplicateFound) {
          onDuplicateFound(
            () => proceedWithVerification(data, setProcessingRowId, duplicatedSeq, response.fileName, false),
            () => proceedWithVerification(data, setProcessingRowId, 0, null, true)
          );
          return;
        }
      }

      // If no duplicate or no callback provided, proceed with upload
      await proceedWithVerification(data, setProcessingRowId, 0, null, false);
    } catch (error) {
      console.error("Error during verification creation:", error);
    }
  };

  const proceedWithVerification = async (data, setProcessingRowId, duplicatedSeq = 0, fileName = null, forceNewScan = false) => {
    const { projectSeq, projectId, projectName, requestComment } = data;

    try {
      // If forceNewScan is true, always perform new scan regardless of duplicate
      const effectiveDuplicatedSeq = forceNewScan ? 0 : duplicatedSeq;
      const isDuplicateWithSameName = !forceNewScan && fileName !== null && fileName === data.verifFile[0].name && duplicatedSeq > 0;
      
      const response = await createClarityAirVerification.mutateAsync({
        projectSeq: parseInt(projectSeq),
        projectId,
        projectName: projectName,
        requestComment,
        duplicatedSeq: effectiveDuplicatedSeq,
        repositoryImportFlag: data.repositoryImportFlag || "N",
        upload: forceNewScan ? "true" : (isDuplicateWithSameName ? "false" : "true"),
      });

      if (typeof response !== "string") {
        const formData = new FormData();
        //let clarityAirVerificationSeq = response?.data?.clarityAirVerificationSeq;
        let clarityAirVerificationSeq = response?.clarityAirVerificationSeq;
        formData.append("clarityAirFile", data.verifFile[0]);
        formData.append("clarityAirVerificationSeq", clarityAirVerificationSeq);
        formData.append("isScan", forceNewScan ? "true" : (effectiveDuplicatedSeq === 0 ? "true" : "false"));


        try {
          if (forceNewScan || !isDuplicateWithSameName) {
              await verificationUpload.mutateAsync(formData);
          }
        } catch (error) {
          console.error("Upload failed:", error);
        }
        setProcessingRowId(clarityAirVerificationSeq);
        await getVerificationData.mutateAsync(projectSeq);
        await pollVerificationStatus(clarityAirVerificationSeq);
        setProcessingRowId(null);
        await getVerificationData.mutateAsync(projectSeq);
      }
    } catch (error) {
      console.error("Error during verification creation:", error);
    }
  };

  const handleReUploadVerification = async (data, onDuplicateFound, onSuccess, setProcessingRowId) => {
    const { requestComment, projectSeq, projectId, verifFile } = data;

    try {
      const maxFileSize = 1024 * 1024 * 1024; // 1GB in bytes

      if (verifFile[0].size > maxFileSize) {
        showErrorToast(t("common.error"), t("project.verification.file-size-exceeded"));
        return;
      }
      const checksumFormData = new FormData();
      checksumFormData.append("file", verifFile[0]);

      const response = await checkDuplicateVerificationWithParams.mutateAsync(checksumFormData);
      const duplicatedSeq = response?.duplicateSeq || 0;

      if (duplicatedSeq > 0) {
        // Call the callback to show confirmation modal with both options
        if (onDuplicateFound) {
          onDuplicateFound(
            () => proceedWithReUpload(data, onSuccess, setProcessingRowId, duplicatedSeq, response.fileName, false),
            () => proceedWithReUpload(data, onSuccess, setProcessingRowId, 0, null, true)
          );
          return;
        }
      }

      // If no duplicate or no callback provided, proceed with upload
      await proceedWithReUpload(data, onSuccess, setProcessingRowId, 0, null, false);
    } catch (error) {
      throw error;
    }
  };

  const proceedWithReUpload = async (data, onSuccess, setProcessingRowId, duplicatedSeq = 0, fileName = null, forceNewScan = false) => {
    const { requestComment, projectId, projectSeq, projectName, verifFile } = data;

    try {
      // If forceNewScan is true, always perform new scan regardless of duplicate
      const effectiveDuplicatedSeq = forceNewScan ? 0 : duplicatedSeq;
      const isDuplicateWithSameName = !forceNewScan && fileName !== null && fileName === verifFile[0].name && duplicatedSeq > 0;
      
      // Create new verification record instead of updating existing one
      const response = await createClarityAirVerification.mutateAsync({
        projectSeq: parseInt(projectSeq),
        projectId,
        projectName: projectName,
        requestComment: requestComment || "",
        duplicatedSeq: effectiveDuplicatedSeq,
        upload: forceNewScan ? "true" : (isDuplicateWithSameName ? "false" : "true"),
        repositoryImportFlag: data.repositoryImportFlag || "N",
      });

      if (typeof response !== "string") {
        // Upload new file using the new verification record
        const formData = new FormData();
        //let clarityAirVerificationSeq = response?.data?.clarityAirVerificationSeq;
        let clarityAirVerificationSeq = response?.clarityAirVerificationSeq;
        formData.append("clarityAirFile", verifFile[0]);
        formData.append("isScan", forceNewScan ? "true" : (effectiveDuplicatedSeq === 0 ? "true" : "false"));
        formData.append("clarityAirVerificationSeq", clarityAirVerificationSeq);

        try {
         if (forceNewScan || !isDuplicateWithSameName) {
            await verificationUpload.mutateAsync(formData);
         }
        } catch (error) {
          console.error("Upload failed:", error);
        }

        // Set processing state briefly (no polling)
        if (setProcessingRowId) {
          setProcessingRowId(clarityAirVerificationSeq);
        }
        
        // Refresh verification data once
        await getVerificationData.mutateAsync(projectSeq);
        
        // Clear processing state immediately
        if (setProcessingRowId) {
          setProcessingRowId(null);
        }

        // Call success callback if provided (this will handle navigation)
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      throw error;
    }
  };

  const createClarityAirVerification = useMutationData(
    queryKeys.createClarityAirVerification,
    {
      method: "POST",
      skipErrorToast: true,
    },
    {
      onError: (error) => {
        const errorMessage = getClarityAirVerificationErrorMessage(error, t);
        useSuccessToast(TOAST.error, t("common.error"), errorMessage);
      },
    },
  );

  const verificationUpload = useMutationData(
    queryKeys.clarityAirVerificationUpload,
    {
      method: "POST",
      skipErrorToast: true,
    },
    {
      onError: (error) => {
        const errorMessage = getVerificationUploadErrorMessage(error, t);
        useSuccessToast(TOAST.error, t("common.error"), errorMessage);
      },
    },
  );

  const getVerificationData = useMutationData(
    (projectSeq) => `${queryKeys.clarityAirVerificationByProjectSeq}?projectSeq=${projectSeq}`,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setVerificationData(normalizeResponseData(response));
        }
      },
      onError: (error) => {
        console.error("Error during verification data fetching:", error);
      },
    },
  );

  const getVerificationStatus = useMutationData(
    (clarityAirVerificationSeq) =>
      `${queryKeys.createClarityAirVerification}/${clarityAirVerificationSeq}`,
    {
      method: "GET",
    },
    {
      onError: (error) => {
        console.error("Error during verification status fetching:", error);
      },
    },
  );

  const pollVerificationStatus = async (clarityAirVerificationSeq) => {
    const seqKey = Number(clarityAirVerificationSeq);
    clearPollTimerForSeq(seqKey);

    return new Promise((resolve) => {
      const scheduleNext = () => {
        const id = setTimeout(tick, 5000);
        pollTimersBySeqRef.current.set(seqKey, id);
      };

      const tick = async () => {
        if (!isMountedRef.current) {
          clearPollTimerForSeq(seqKey);
          return;
        }

        try {
          const statusResponse = await getVerificationStatus.mutateAsync(clarityAirVerificationSeq);

          if (!statusResponse) {
            if (!isMountedRef.current) {
              clearPollTimerForSeq(seqKey);
              return;
            }
            scheduleNext();
            return;
          }

          const { verificationStatusCd, clarityAirScanStatusCd, clarityAirScannedFlag } = statusResponse;

          const isSuccess =
            verificationStatusCd === "ON_VERIFICATION" &&
            clarityAirScanStatusCd === "success" &&
            clarityAirScannedFlag === "Y";

          const isAnalysisCheck = verificationStatusCd === "ANALYSIS_CHECK";
          const isError = verificationStatusCd === "ERROR";

          if (isSuccess || isError || isAnalysisCheck) {
            clearPollTimerForSeq(seqKey);
            if (isMountedRef.current) {
              resolve(statusResponse);
            }
            return;
          }
        } catch (error) {
          if (error?.response?.status === 401) {
            clearPollTimerForSeq(seqKey);
            cleanup();
            return;
          }
          console.error("Error polling verification status:", error);
        }

        if (!isMountedRef.current) {
          clearPollTimerForSeq(seqKey);
          return;
        }
        scheduleNext();
      };

      scheduleNext();
    });
  };

  return {
    verificationUpload,
    handleSubmitVerificationRequest,
    handleReUploadVerification,
    verificationData,
    getVerificationData,
    checkDuplicateVerification,
    checkDuplicateVerificationWithParams,
    isDuplicateCheckPending:
      checkDuplicateVerificationWithParams?.isPending ??
      checkDuplicateVerification?.isPending ??
      false,
    isCreateVerificationPending: createClarityAirVerification?.isPending ?? false,
    cleanup, // Export cleanup function for manual cleanup
  };
};
